// Marcações (tecla M): IN, depois OUT, e o editor flutuante pede o que mudar no
// trecho. Diferente do editor antigo, aqui elas viram TAREFA: "Aplicar marcações
// com IA" manda as abertas para POST /jobs/:id/notes e a IA refaz o trecho.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EdvidNote } from '../../../types/videoEdit';
import { fmt } from './edvidTime';
import { useTimeline } from './TimelineShell';
import { IconClose, IconSpark } from './icons';

export type NotePhase = EdvidNote['phase'];

export function useEdvidNotes(serverNotes: EdvidNote[] | undefined) {
  const [local, setLocal] = useState<EdvidNote[]>([]);
  const [pendingIn, setPendingIn] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ id: string; isNew: boolean } | null>(null);

  // o servidor devolveu uma marcação que estava só aqui → some da lista local
  useEffect(() => {
    if (!serverNotes?.length) return;
    const ids = new Set(serverNotes.map(n => n.id));
    setLocal(prev => (prev.some(n => ids.has(n.id)) ? prev.filter(n => !ids.has(n.id)) : prev));
  }, [serverNotes]);

  const all = [...(serverNotes || []), ...local].sort((a, b) => a.start - b.start);

  /** IN / OUT. `t` no tempo da timeline da aba; `toRendered` leva ao MP4 que o usuário via. */
  const toggleMark = useCallback((t: number, phase: NotePhase, toRendered: (t: number) => number): string | null => {
    if (pendingIn == null) {
      setPendingIn(t);
      return 'IN marcado — leve a agulha ao fim do trecho e marque o OUT';
    }
    const start = Math.min(pendingIn, t);
    const end = Math.max(pendingIn, t);
    if (end - start < 0.05) return 'Trecho curto demais — afaste a agulha do IN';
    setPendingIn(null);
    const note: EdvidNote = {
      id: `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      start: +start.toFixed(3),
      end: +end.toFixed(3),
      renderedStart: +toRendered(start).toFixed(3),
      renderedEnd: +toRendered(end).toFixed(3),
      phase,
      text: '',
      status: 'open',
      createdAt: new Date().toISOString(),
    };
    setLocal(prev => [...prev, note]);
    setEditing({ id: note.id, isNew: true });
    return null;
  }, [pendingIn]);

  const updateText = (id: string, text: string) => setLocal(prev => prev.map(n => (n.id === id ? { ...n, text } : n)));
  const remove = (id: string) => setLocal(prev => prev.filter(n => n.id !== id));
  const isLocal = (id: string) => local.some(n => n.id === id);

  return {
    all, local, pendingIn, setPendingIn, editing, setEditing, toggleMark, updateText, remove, isLocal,
    clearLocal: () => setLocal([]),
  };
}

export function NotesLane({ notes, pendingIn, onOpen }: {
  notes: EdvidNote[];
  pendingIn: number | null;
  onOpen: (id: string) => void;
}) {
  const { pps } = useTimeline();
  return (
    <>
      {notes.map(n => (
        <button
          key={n.id}
          type="button"
          data-tl-item
          onClick={() => onOpen(n.id)}
          className={`absolute top-[3px] bottom-[3px] truncate rounded-md border px-1.5 text-left text-[10px] font-bold ${n.status === 'open' ? 'border-rose-400/50 bg-rose-500/20 text-rose-100 hover:bg-rose-500/35' : 'border-white/10 bg-white/5 text-slate-500 line-through'}`}
          style={{ left: n.start * pps, width: Math.max((n.end - n.start) * pps, 10) }}
          title={`${fmt(n.start)} → ${fmt(n.end)}\n${n.text || '(sem descrição)'}\n\nclique para editar`}
        >
          {n.text || '(sem descrição)'}
        </button>
      ))}
      {pendingIn != null && (
        <div className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-rose-400" style={{ left: pendingIn * pps }}>
          <span className="absolute -top-0.5 left-1 rounded bg-rose-500 px-1 text-[9px] font-black text-white">IN</span>
        </div>
      )}
    </>
  );
}

export function NoteEditor({ note, isNew, editable, onSave, onDelete, onClose }: {
  note: EdvidNote;
  isNew: boolean;
  editable: boolean;
  onSave: (text: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(note.text);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const save = () => { if (text.trim()) onSave(text.trim()); };
  return (
    <div className="fixed inset-0 z-[2147483100] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 p-4 shadow-2xl" role="dialog" aria-label="Marcação">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-xs font-bold text-rose-200">{fmt(note.start)} → {fmt(note.end)}</span>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10" title="Fechar" aria-label="Fechar"><IconClose /></button>
        </div>
        <textarea
          ref={ref}
          value={text}
          readOnly={!editable}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
          }}
          rows={3}
          placeholder="O que precisa mudar nesse trecho? Ex.: “corta o gaguejo”, “insere o logo”, “abaixa a trilha”."
          className="w-full resize-none rounded-xl border border-white/10 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-indigo-500"
        />
        <div className="mt-3 flex items-center gap-2">
          {!isNew && editable && (
            <button type="button" onClick={onDelete} className="rounded-xl px-3 py-2 text-xs font-black text-rose-300 hover:bg-rose-500/10">Remover</button>
          )}
          <span className="text-[10px] text-slate-500">Ctrl+Enter salva</span>
          <button
            type="button"
            onClick={save}
            disabled={!editable || !text.trim()}
            className="ml-auto rounded-xl bg-indigo-500 px-4 py-2 text-xs font-black text-white hover:bg-indigo-400 disabled:opacity-40"
          >
            Salvar marcação
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<EdvidNote['status'], string> = { open: 'aberta', applied: 'aplicada', dismissed: 'descartada' };

export function NotesPanel({ notes, phase, isLocal, onOpen, onSeek, onApply, applying }: {
  notes: EdvidNote[];
  phase: NotePhase;
  isLocal: (id: string) => boolean;
  onOpen: (id: string) => void;
  onSeek: (t: number) => void;
  onApply: () => void;
  applying: boolean;
}) {
  const mine = notes.filter(n => n.phase === phase);
  const open = mine.filter(n => n.status === 'open' && n.text.trim());
  return (
    <section className="rounded-2xl border border-white/10 bg-slate-950/70 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Marcações ({mine.length})</p>
        <button
          type="button"
          onClick={onApply}
          disabled={!open.length || applying}
          className="flex items-center gap-1.5 rounded-xl border border-indigo-400/40 bg-indigo-500/15 px-3 py-1.5 text-xs font-black text-indigo-200 hover:bg-indigo-500/25 disabled:opacity-40"
        >
          <IconSpark width={12} height={12} />
          {applying ? 'Enviando…' : 'Aplicar marcações com IA'}
        </button>
      </div>
      {!mine.length ? (
        <p className="text-[11px] text-slate-500">Aperte <kbd className="rounded bg-white/10 px-1 font-mono">M</kbd> no início e no fim de um trecho e escreva o que mudar.</p>
      ) : (
        <ul className="space-y-1">
          {mine.map(n => (
            <li key={n.id} className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-white/5">
              <button type="button" onClick={() => onSeek(n.start)} className="font-mono text-[10px] text-slate-400 hover:text-white">{fmt(n.start)}</button>
              <button type="button" onClick={() => onOpen(n.id)} className="min-w-0 flex-1 truncate text-left text-xs text-slate-200">{n.text || '(sem descrição)'}</button>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${n.status === 'open' ? (isLocal(n.id) ? 'bg-amber-500/15 text-amber-300' : 'bg-rose-500/15 text-rose-300') : n.status === 'applied' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-slate-500'}`}>
                {n.status === 'open' && isLocal(n.id) ? 'não enviada' : STATUS_LABEL[n.status]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export type EdvidNotesApi = ReturnType<typeof useEdvidNotes>;
